// HOME SCREEN
const HOME_PERIOD_DAYS = 30;
const HOME_DAY_MS = 24 * 60 * 60 * 1000;
const SOURCE_WARNING_MS = 24 * 60 * 60 * 1000;
const SOURCE_ERROR_MS = 72 * 60 * 60 * 1000;
const HOME_TONE_STYLES = {
  neutral: { border: '#E2E8F0', bg: '#F8FAFC', color: '#475569', icon: '#64748B' },
  warning: { border: '#FDE68A', bg: '#FFFBEB', color: '#92400E', icon: '#D97706' },
  error: { border: '#FECACA', bg: '#FEF2F2', color: '#991B1B', icon: '#DC2626' },
};

const parseHomeDate = value => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const orderAmountNumber = order => {
  const amount = parseFloat(String(order?.amount || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
};

const startOfLocalDay = date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const startOfLocalWeek = date => {
  const d = startOfLocalDay(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
};

const revenueRowsForOrders = orders => Object.values(orders.reduce((acc, order) => {
  const amount = orderAmountNumber(order);
  if (!amount) return acc;
  const currency = (order.currency || 'usd').toLowerCase();
  if (!acc[currency]) acc[currency] = { currency, revenue: 0, count: 0 };
  acc[currency].revenue += amount;
  acc[currency].count += 1;
  return acc;
}, {}));

const trendDelta = (current, previous) => {
  const cur = Number(current || 0);
  const prev = Number(previous || 0);
  let pct = 0;
  if (prev === 0 && cur > 0) pct = 100;
  else if (prev !== 0) pct = ((cur - prev) / Math.abs(prev)) * 100;
  const rounded = Math.round(Math.abs(pct));
  if (pct > 0) return { text: `▲ ${rounded}%`, direction: 'up', color: '#475569' };
  if (pct < 0) return { text: `▼ ${rounded}%`, direction: 'down', color: '#B45309' };
  return { text: '0%', direction: 'flat', color: '#64748B' };
};

const homeDateKey = date => {
  const d = startOfLocalDay(date);
  return d.toISOString().slice(0, 10);
};

const formatHomeCount = value => Number(value || 0).toLocaleString('en-US');

const HomeSourceIcon = ({ source, size = 22 }) => {
  const cfg = PLATFORM_CFG[source] || { label: source || 'Source', color: '#6B7280', char: '?' };
  const Icon = cfg.Icon;
  return (
    <span title={cfg.label} style={{ width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {Icon
        ? <Icon size={size - 2} strokeWidth={2.1} color={cfg.color}/>
        : cfg.logo
          ? <img src={cfg.logo} alt={cfg.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
          : <span style={{ color: cfg.color, fontWeight: 900, fontSize: Math.max(10, size - 10), lineHeight: 1 }}>{cfg.char}</span>
      }
    </span>
  );
};

const sourceHealthStatus = (source, now) => {
  if (source.lastError || source.state === 'FAILED') {
    return { key: 'error', label: 'Error', Icon: XCircle, message: source.lastError || 'Connection needs attention' };
  }
  if (!source.connected) {
    return { key: 'warning', label: 'Warning', Icon: AlertTriangle, message: source.state === 'AWAITING_ACTIVATION' ? 'Awaiting activation' : 'Setup incomplete' };
  }
  const syncedAt = parseHomeDate(source.lastSyncedAt);
  if (!syncedAt) return { key: 'warning', label: 'Warning', Icon: AlertTriangle, message: 'Never synced' };
  const age = now.getTime() - syncedAt.getTime();
  if (age > SOURCE_ERROR_MS) return { key: 'error', label: 'Error', Icon: XCircle, message: 'Sync is stale' };
  if (age > SOURCE_WARNING_MS) return { key: 'warning', label: 'Warning', Icon: AlertTriangle, message: 'Sync is aging' };
  return { key: 'healthy', label: 'Healthy', Icon: CheckCircle2, message: 'Sync current' };
};

const HomeKpiCard = ({ card }) => {
  const Wrapper = card.onClick ? 'button' : 'div';
  return (
  <Wrapper {...(card.onClick ? { type: 'button', onClick: card.onClick } : {})} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '13px 14px', boxShadow: '0 1px 2px rgba(15,23,42,0.03)', minWidth: 0, textAlign: 'left', cursor: card.onClick ? 'pointer' : 'default', width: '100%' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
        {card.Icon && <card.Icon size={14} strokeWidth={2.2} color={card.iconColor || '#64748B'} style={{ flexShrink: 0 }}/>}
        <div style={{ fontSize: 11, fontWeight: 850, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.label}</div>
      </div>
      {card.onClick && <ArrowRight size={13} strokeWidth={2.3} color="#94A3B8" style={{ flexShrink: 0 }}/>}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
      <div style={{ fontSize: 26, fontWeight: 900, color: card.valueColor || '#0F172A', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>
        {card.value === null ? <SkeletonPill width={card.skeletonWidth || 54} height={25}/> : card.value}
      </div>
      {card.extra}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 7, minHeight: 15 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#94A3B8', minWidth: 0 }}>
        {card.windowed ? <Clock3 size={12} strokeWidth={2}/> : <Download size={12} strokeWidth={2}/>}
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.meta}</span>
      </div>
      {card.trend && (
        <div title="Compared with prior 30 days" style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: 12, fontWeight: 900, color: card.trend.color }}>{card.trend.text}</span>
          <span style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 700 }}>vs prior 30d</span>
        </div>
      )}
      {!card.trend && card.trendLabel && (
        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: 12, fontWeight: 900, color: '#64748B' }}>{card.trendLabel}</span>
        </div>
      )}
    </div>
  </Wrapper>
  );
};

const HomeScreen = ({ user, orders, ordersLoading = false, ordersError = '', onNavigate, onRefresh, onUserChange }) => {
  const [now, setNow] = useState(() => new Date());
  const [checkoutSuccess] = useState(() => new URLSearchParams(window.location.search).get('checkout') === 'success');
  const [showCheckoutTutorial, setShowCheckoutTutorial] = useState(() => new URLSearchParams(window.location.search).get('checkout') === 'success');
  const [sourceActions, setSourceActions] = useState({});
  const [sourceOverrides, setSourceOverrides] = useState({});
  const [lastHomeSeenAt] = useState(() => {
    try {
      const saved = localStorage.getItem('p2s_home_last_seen_at');
      return saved ? new Date(saved) : new Date();
    } catch {
      return new Date();
    }
  });
  const lastRefreshRef = useRef(Date.now());

  useEffect(() => {
    try { localStorage.setItem('p2s_home_last_seen_at', new Date().toISOString()); } catch {}
    const tick = setInterval(() => setNow(new Date()), 30 * 1000);
    const refreshIfStale = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshRef.current < 5 * 60 * 1000) return;
      lastRefreshRef.current = Date.now();
      onRefresh?.();
      onUserChange?.();
    };
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', refreshIfStale);
    return () => {
      clearInterval(tick);
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', refreshIfStale);
    };
  }, [onRefresh, onUserChange]);

  const navigateWithQuery = (screen, query = '') => {
    const path = screen === 'home' ? '/' : `/${screen}${query ? `?${query}` : ''}`;
    window.history.pushState({}, '', path);
    onNavigate?.(screen);
  };

  const activeOrders = orders.filter(order => !isIgnoredDashboardOrder(order));
  const sortedOrders = [...activeOrders].sort((a, b) => {
    const bd = parseOrderDate(b)?.getTime() || 0;
    const ad = parseOrderDate(a)?.getTime() || 0;
    if (bd !== ad) return bd - ad;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });
  const lastOrders = sortedOrders.slice(0, 12);

  const currentStart = new Date(now.getTime() - HOME_PERIOD_DAYS * HOME_DAY_MS);
  const priorStart = new Date(currentStart.getTime() - HOME_PERIOD_DAYS * HOME_DAY_MS);
  const currentPeriodOrders = activeOrders.filter(order => {
    const d = parseOrderDate(order);
    return d && d >= currentStart && d <= now;
  });
  const priorPeriodOrders = activeOrders.filter(order => {
    const d = parseOrderDate(order);
    return d && d >= priorStart && d < currentStart;
  });
  const currentRevenueRows = revenueRowsForOrders(currentPeriodOrders);
  const priorRevenueRows = revenueRowsForOrders(priorPeriodOrders);
  const sortedCurrentRevenueRows = [...currentRevenueRows].sort((a, b) => b.count - a.count || Math.abs(b.revenue) - Math.abs(a.revenue));
  const sortedPriorRevenueRows = [...priorRevenueRows].sort((a, b) => b.count - a.count || Math.abs(b.revenue) - Math.abs(a.revenue));
  const convertedRevenueMixing = user?.convertedRevenueMixing !== false;
  const preferredRevenueCurrency = user?.preferredAnalyticsCurrency || null;
  const revenueCurrency = preferredRevenueCurrency || sortedCurrentRevenueRows[0]?.currency || sortedPriorRevenueRows[0]?.currency || 'usd';
  const [homeExchangeRates, setHomeExchangeRates] = useState(null);
  useEffect(() => {
    let active = true;
    if (!convertedRevenueMixing) {
      setHomeExchangeRates({ [revenueCurrency]:1 });
      return () => { active = false; };
    }
    setHomeExchangeRates(null);
    api('GET', `/api/analytics/exchange-rates?base=${encodeURIComponent(revenueCurrency)}`).then(({ ok, data }) => {
      if (!active) return;
      setHomeExchangeRates(ok && data?.rates ? data.rates : { [revenueCurrency]:1 });
    }).catch(() => {
      if (active) setHomeExchangeRates({ [revenueCurrency]:1 });
    });
    return () => { active = false; };
  }, [convertedRevenueMixing, revenueCurrency]);
  const convertedHomeOrderAmount = order => {
    const currency = (order.currency || 'usd').toLowerCase();
    const amount = orderAmountNumber(order);
    if (currency === revenueCurrency) return amount;
    if (!convertedRevenueMixing) return 0;
    const rate = Number(homeExchangeRates?.[currency]);
    return Number.isFinite(rate) && rate > 0 ? amount * rate : 0;
  };
  const currentRevenue = currentPeriodOrders.reduce((sum, order) => sum + convertedHomeOrderAmount(order), 0);
  const priorRevenue = priorPeriodOrders.reduce((sum, order) => sum + convertedHomeOrderAmount(order), 0);
  const currentRevenueOrderCount = convertedRevenueMixing
    ? currentPeriodOrders.filter(order => order.amount !== null && order.amount !== '').length
    : currentPeriodOrders.filter(order => (order.currency || 'usd').toLowerCase() === revenueCurrency).length;
  const priorRevenueOrderCount = convertedRevenueMixing
    ? priorPeriodOrders.filter(order => order.amount !== null && order.amount !== '').length
    : priorPeriodOrders.filter(order => (order.currency || 'usd').toLowerCase() === revenueCurrency).length;
  const currentAov = currentRevenueOrderCount ? currentRevenue / currentRevenueOrderCount : 0;
  const priorAov = priorRevenueOrderCount ? priorRevenue / priorRevenueOrderCount : 0;
  const hasForeignRevenue = [...currentRevenueRows, ...priorRevenueRows].some(row => row.currency !== revenueCurrency);
  const revenueLoading = ordersLoading || (convertedRevenueMixing && hasForeignRevenue && homeExchangeRates === null);

  const dailyRevenue = Array.from({ length: HOME_PERIOD_DAYS }, (_, idx) => {
    const day = startOfLocalDay(new Date(now.getTime() - (HOME_PERIOD_DAYS - 1 - idx) * HOME_DAY_MS));
    const nextDay = new Date(day.getTime() + HOME_DAY_MS);
    const revenue = currentPeriodOrders.reduce((sum, order) => {
      const d = parseOrderDate(order);
      if (!d || d < day || d >= nextDay) return sum;
      return sum + convertedHomeOrderAmount(order);
    }, 0);
    return { key: homeDateKey(day), date: day, revenue };
  });
  const maxDailyRevenue = Math.max(...dailyRevenue.map(point => point.revenue), 1);

  const rawSourceHealth = user?.sourceHealth || {};
  const automaticSources = [
    {
      id: 'stripe',
      connected: rawSourceHealth.stripe?.connected ?? !!user?.hasStripeConnected,
      lastSyncedAt: rawSourceHealth.stripe?.lastSyncedAt,
      syncPath: '/api/stripe/sync',
      syncBody: { mode: 'checkout_sessions' },
    },
    {
      id: 'paypal',
      connected: rawSourceHealth.paypal?.connected ?? !!user?.hasPayPalConnected,
      lastSyncedAt: rawSourceHealth.paypal?.lastSyncedAt,
      syncPath: '/api/paypal/sync',
      syncBody: {},
    },
    {
      id: 'shopify',
      connected: rawSourceHealth.shopify?.connected ?? !!user?.hasShopifyConnected,
      lastSyncedAt: rawSourceHealth.shopify?.lastSyncedAt,
      syncPath: '/api/shopify/sync',
      syncBody: {},
    },
    {
      id: 'woocommerce',
      connected: rawSourceHealth.woocommerce?.connected ?? !!user?.hasWooCommerceConnected,
      lastSyncedAt: rawSourceHealth.woocommerce?.lastSyncedAt,
      syncPath: '/api/woocommerce/sync',
      syncBody: {},
    },
    {
      id: 'adobe_commerce',
      connected: rawSourceHealth.adobe_commerce?.connected ?? !!user?.hasAdobeCommerceConnected,
      state: rawSourceHealth.adobe_commerce?.state,
      lastError: rawSourceHealth.adobe_commerce?.lastError,
      lastSyncedAt: rawSourceHealth.adobe_commerce?.lastSyncedAt,
      connectionId: rawSourceHealth.adobe_commerce?.id,
      syncPath: rawSourceHealth.adobe_commerce?.id ? `/api/connectors/adobe-commerce/${rawSourceHealth.adobe_commerce.id}/sync` : '',
      syncBody: {},
    },
  ].map(source => ({ ...source, ...(sourceOverrides[source.id] || {}) }));
  const visibleSources = automaticSources
    .filter(source => source.connected || source.lastError || (source.id === 'adobe_commerce' && source.connectionId))
    .map(source => ({ ...source, health: sourceHealthStatus(source, now) }));
  const sourceCounts = visibleSources.reduce((acc, source) => {
    acc.total += 1;
    acc[source.health.key] = (acc[source.health.key] || 0) + 1;
    return acc;
  }, { total: 0, healthy: 0, warning: 0, error: 0 });
  const sourceOverallHealth = sourceCounts.error ? 'error' : sourceCounts.warning || sourceCounts.total === 0 ? 'warning' : 'healthy';
  const failingSourceCount = sourceCounts.error || 0;

  const unmappedSkuKeys = new Set();
  activeOrders.forEach(order => {
    orderItemsForDisplay(order).forEach(item => {
      const rawName = normalizeProductName(item.raw_name || item.product || item.display_name);
      if (!rawName || item.product_id) return;
      unmappedSkuKeys.add(`${order.source || 'source'}:${item.sku || rawName}`);
    });
  });
  const unmappedSkuCount = unmappedSkuKeys.size;
  const reviewOrderCount = activeOrders.filter(order => {
    const status = normalizeDashboardOrderStatus(order.status);
    if (status === 'problem' || order.duplicate_review_status === 'suspected') return true;
    if (['invalid', 'review'].includes(order.address_check_status) || !String(order.phone || '').trim()) return true;
    const recipientName = [order.first_name, order.last_name].map(value => String(value || '').trim()).filter(Boolean).join(' ');
    return !!recipientName && (
      /\d/.test(recipientName)
      || (String(order.address_line1 || '').trim() && recipientName.toLowerCase() === String(order.address_line1).trim().toLowerCase())
    );
  }).length;
  const todayStart = startOfLocalDay(now);
  const weekStart = startOfLocalWeek(now);
  const exportedOrders = activeOrders.filter(order => normalizeDashboardOrderStatus(order.status) === 'routed');
  const exportedTodayCount = exportedOrders.filter(order => {
    const d = parseHomeDate(order.updated_at) || parseOrderDate(order);
    return d && d >= todayStart && d <= now;
  }).length;
  const exportedWeekCount = exportedOrders.filter(order => {
    const d = parseHomeDate(order.updated_at) || parseOrderDate(order);
    return d && d >= weekStart && d <= now;
  }).length;
  const readyToExportCount = activeOrders.filter(order => (
    normalizeDashboardOrderStatus(order.status) === 'paid'
    && !orderNeedsProductDefinition(order)
  )).length;
  const needsRows = ordersLoading ? [] : [
    {
      key: 'products',
      label: 'Products',
      detail: 'unmapped SKUs',
      count: unmappedSkuCount,
      Icon: Inbox,
      screen: 'products',
      query: 'filter=unmapped',
      tone: 'warning',
    },
    {
      key: 'orders',
      label: 'Orders',
      detail: 'problem or possible duplicate',
      count: reviewOrderCount,
      Icon: Package,
      screen: 'orders',
      query: 'filter=review',
      tone: 'error',
    },
    {
      key: 'sources',
      label: 'Sources',
      detail: 'failing',
      count: failingSourceCount,
      Icon: Upload,
      screen: 'sources',
      query: 'filter=error',
      tone: 'error',
    },
  ].filter(row => row.count > 0);
  const needsAttentionTotal = ordersLoading ? null : needsRows.reduce((sum, row) => sum + row.count, 0);
  const needsHealth = needsAttentionTotal === null ? null : failingSourceCount > 0 ? 'error' : needsAttentionTotal > 0 ? 'warning' : 'healthy';
  const primaryNeedsRow = needsRows.find(row => row.key === 'sources') || needsRows.find(row => row.key === 'orders') || needsRows[0] || null;
  const productTotals = new Map();
  currentPeriodOrders.forEach(order => {
    const items = orderItemsForDisplay(order);
    const totalQty = items.reduce((sum, item) => sum + (Number(item.quantity || 1) || 1), 0) || 1;
    const amount = convertedHomeOrderAmount(order);
    items.forEach(item => {
      const qty = Number(item.quantity || 1) || 1;
      const name = normalizeProductName(item.display_name || item.product_name || item.raw_name || item.product) || 'Unlabeled product';
      const prev = productTotals.get(name) || { name, count: 0, revenue: 0 };
      prev.count += qty;
      prev.revenue += amount * (qty / totalQty);
      productTotals.set(name, prev);
    });
  });
  const topProducts = [...productTotals.values()]
    .sort((a, b) => b.revenue - a.revenue || b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, 5);
  const recentExports = exportedOrders
    .map(order => ({ order, exportedAt: parseHomeDate(order.updated_at) || parseOrderDate(order) }))
    .filter(item => item.exportedAt)
    .sort((a, b) => b.exportedAt.getTime() - a.exportedAt.getTime())
    .slice(0, 4);

  const sub = user?.subscription || {};
  const trialEnd = sub.currentPeriodEnd ? new Date(`${sub.currentPeriodEnd}T00:00:00Z`) : null;
  const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / HOME_DAY_MS)) : null;
  const billingAlert = ['past_due', 'unpaid', 'incomplete', 'expired'].includes(sub.status)
    ? {
        tone: 'error',
        title: 'Billing needs attention',
        detail: 'Open Settings to review your subscription status.',
      }
    : (['trialing'].includes(sub.status) && trialDaysLeft !== null && trialDaysLeft <= 3)
      ? {
          tone: 'warning',
          title: 'Trial ending soon',
          detail: `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in your trial.`,
        }
      : null;

  const handleSourceAction = async source => {
    const shouldReconnect = source.state === 'FAILED' || !source.connected || !source.syncPath;
    if (shouldReconnect) {
      navigateWithQuery('sources', `source=${encodeURIComponent(source.id)}`);
      return;
    }
    setSourceActions(prev => ({ ...prev, [source.id]: { status: 'syncing', error: '' } }));
    const { ok, data } = await api('POST', source.syncPath, source.syncBody || {});
    if (ok) {
      setSourceOverrides(prev => ({ ...prev, [source.id]: { lastSyncedAt: new Date().toISOString(), lastError: '' } }));
      setSourceActions(prev => ({ ...prev, [source.id]: { status: 'ok', error: '' } }));
      onRefresh?.();
      onUserChange?.();
    } else {
      const message = data?.error || 'Sync failed';
      setSourceOverrides(prev => ({ ...prev, [source.id]: { ...(prev[source.id] || {}), lastError: message } }));
      setSourceActions(prev => ({ ...prev, [source.id]: { status: 'error', error: message } }));
    }
  };

  const closeCheckoutTutorial = () => {
    setShowCheckoutTutorial(false);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  };
  const startCheckoutTutorial = () => {
    closeCheckoutTutorial();
    onNavigate('setup');
  };

  const sourceIssue = visibleSources.find(source => source.health.key === 'error')
    || visibleSources.find(source => source.health.key === 'warning')
    || null;
  const sourceTone = sourceOverallHealth === 'error' ? 'error' : sourceOverallHealth === 'warning' ? 'warning' : 'neutral';
  const sourceToneStyle = HOME_TONE_STYLES[sourceTone];
  const sourceSyncDates = visibleSources.map(source => parseHomeDate(source.lastSyncedAt)).filter(Boolean);
  const oldestSourceSync = sourceSyncDates.length
    ? new Date(Math.min(...sourceSyncDates.map(date => date.getTime())))
    : null;
  const sourceStatusDetail = sourceCounts.total
    ? sourceIssue
      ? `${sourceIssue.health.message || 'Source needs attention'}`
      : `all synced ${oldestSourceSync ? timeAgo(oldestSourceSync, now) : 'recently'}`
    : 'connect a live source';
  const attentionTone = needsHealth === 'error' ? 'error' : needsHealth === 'warning' ? 'warning' : 'neutral';
  const attentionToneStyle = HOME_TONE_STYLES[attentionTone];
  const attentionSummary = needsAttentionTotal === null
    ? 'Checking blockers'
    : needsAttentionTotal > 0
      ? `${formatHomeCount(needsAttentionTotal)} item${needsAttentionTotal === 1 ? '' : 's'} need review`
      : 'No blockers';
  const attentionDetail = needsAttentionTotal === null
    ? 'loading order and product state'
    : needsRows.length
      ? needsRows.map(row => `${formatHomeCount(row.count)} ${row.detail}`).join(' · ')
      : 'nothing blocking fulfillment';

  const metricCards = [
    {
      label: 'Revenue',
      value: revenueLoading ? null : formatMoneyCompact(currentRevenue, revenueCurrency),
      meta: 'last 30 days',
      Icon: DollarSign,
      windowed: true,
      trend: revenueLoading ? null : trendDelta(currentRevenue, priorRevenue),
      extra: !revenueLoading ? <CurrencyBreakdownBadge rows={currentRevenueRows}/> : null,
      skeletonWidth: 76,
    },
    {
      label: 'Orders',
      value: ordersLoading ? null : formatHomeCount(currentPeriodOrders.length),
      meta: 'last 30 days',
      Icon: Package,
      windowed: true,
      trend: ordersLoading ? null : trendDelta(currentPeriodOrders.length, priorPeriodOrders.length),
      skeletonWidth: 42,
    },
    {
      label: 'Avg order value',
      value: revenueLoading ? null : formatMoneyCompact(currentAov, revenueCurrency),
      meta: 'last 30 days',
      Icon: DollarSign,
      windowed: true,
      trend: revenueLoading ? null : trendDelta(currentAov, priorAov),
      skeletonWidth: 76,
    },
    {
      label: 'Ready to export',
      value: ordersLoading ? null : formatHomeCount(readyToExportCount),
      meta: 'right now',
      Icon: Download,
      windowed: false,
      trendLabel: 'current',
      onClick: () => navigateWithQuery('export', 'filter=ready'),
      skeletonWidth: 50,
    },
  ];

  const confetti = [
    { left:'8%', color:'#006FDD', delay:'0ms', duration:'980ms', width:7, height:12, xStart:'-10px', xEnd:'24px', spin:'310deg' },
    { left:'16%', color:'#73D04B', delay:'80ms', duration:'1180ms', width:9, height:9, xStart:'8px', xEnd:'-22px', spin:'260deg' },
    { left:'24%', color:'#F59E0B', delay:'30ms', duration:'1080ms', width:6, height:14, xStart:'-6px', xEnd:'18px', spin:'420deg' },
    { left:'34%', color:'#8B5CF6', delay:'130ms', duration:'1240ms', width:8, height:8, xStart:'12px', xEnd:'-18px', spin:'330deg' },
    { left:'45%', color:'#06B6D4', delay:'20ms', duration:'1120ms', width:7, height:13, xStart:'-12px', xEnd:'20px', spin:'380deg' },
    { left:'56%', color:'#73D04B', delay:'110ms', duration:'1030ms', width:10, height:7, xStart:'10px', xEnd:'-24px', spin:'290deg' },
    { left:'66%', color:'#EF4444', delay:'50ms', duration:'1210ms', width:6, height:13, xStart:'-8px', xEnd:'26px', spin:'430deg' },
    { left:'76%', color:'#006FDD', delay:'160ms', duration:'1100ms', width:9, height:9, xStart:'8px', xEnd:'-18px', spin:'340deg' },
    { left:'86%', color:'#F59E0B', delay:'70ms', duration:'1160ms', width:7, height:12, xStart:'-6px', xEnd:'18px', spin:'400deg' },
    { left:'93%', color:'#8B5CF6', delay:'120ms', duration:'1000ms', width:8, height:8, xStart:'6px', xEnd:'-20px', spin:'300deg' },
  ];

  return (
    <div style={{
      padding: '0 0 24px',
      maxWidth: 'none',
      margin: 0,
      minHeight: 'calc(100vh - 64px)',
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, #F8FBFF 0%, #F8FAFC 48%, #FFFFFF 100%)',
    }}>
      <svg className="home-brush-layer" aria-hidden="true" viewBox="0 0 1200 740" preserveAspectRatio="none">
        <path className="home-brush-stripe home-brush-stripe-blue home-brush-stripe-a" d="M-120 170 L145 58 L350 205 L585 82 L805 250 L1115 116 L1320 265"/>
        <path className="home-brush-stripe home-brush-stripe-green home-brush-stripe-b" d="M-130 555 L130 370 L340 500 L560 315 L785 450 L1040 285 L1330 420"/>
        <path className="home-brush-stripe home-brush-stripe-blue home-brush-stripe-c" d="M40 785 L265 620 L505 710 L715 530 L940 670 L1240 505"/>
      </svg>

      {showCheckoutTutorial && (
        <>
          <div onClick={closeCheckoutTutorial} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.38)', zIndex:80 }}/>
          <div aria-hidden="true" style={{ position:'fixed', inset:'0 0 auto 0', height:360, zIndex:85, pointerEvents:'none', overflow:'hidden' }}>
            {confetti.map((piece, idx) => (
              <span
                key={idx}
                className="checkout-confetti-piece"
                style={{
                  position:'absolute',
                  top:0,
                  left:piece.left,
                  width:piece.width,
                  height:piece.height,
                  borderRadius:piece.height === piece.width ? '50%' : 2,
                  background:piece.color,
                  '--x-start':piece.xStart,
                  '--x-end':piece.xEnd,
                  '--spin':piece.spin,
                  animation:`confettiFall ${piece.duration} ease-out ${piece.delay} 1 both`,
                }}
              />
            ))}
          </div>
          <div className="checkout-tutorial-modal" style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(520px, calc(100vw - 32px))', background:'#fff', border:'1px solid #E5E7EB', borderRadius:14, boxShadow:'0 24px 80px rgba(0,0,0,0.22)', zIndex:90, overflow:'hidden', animation:'modalRise 180ms ease-out both' }}>
            <div style={{ padding:'22px 24px 18px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                <div style={{ width:34, height:34, borderRadius:9, background:'#F0FDF4', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <CheckCircle2 size={18} strokeWidth={2.2} color="#16A34A"/>
                </div>
                <div>
                  <div style={{ fontSize:18, fontWeight:800, color:'#1A1A2E', letterSpacing:'-0.2px' }}>Welcome to Omnifill</div>
                  <div style={{ fontSize:13, color:'#6B7280', marginTop:3 }}>Checkout is complete.</div>
                </div>
              </div>
              <button onClick={closeCheckoutTutorial} aria-label="Close tutorial" style={{ border:'none', background:'#F9FAFB', color:'#6B7280', width:30, height:30, borderRadius:8, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <X size={15} strokeWidth={2}/>
              </button>
            </div>
            <div style={{ padding:'22px 24px' }}>
              <div style={{ display:'grid', gap:12, marginBottom:22 }}>
                {[
                  { Icon: Upload, title:'Connect a source', desc:'Start with Stripe, PayPal, Shopify, WooCommerce, or a crowdfunding CSV.' },
                  { Icon: Package, title:'Review normalized orders', desc:'Omnifill turns source-specific order data into one clean order table.' },
                  { Icon: Download, title:'Export for fulfillment', desc:'Send a warehouse-ready CSV or prepare orders for your 3PL workflow.' },
                ].map(({ Icon, title, desc }, idx) => (
                  <div key={title} style={{ display:'grid', gridTemplateColumns:'34px 1fr', gap:12, alignItems:'start' }}>
                    <div style={{ width:34, height:34, borderRadius:9, background: idx === 0 ? '#EEF6FF' : idx === 1 ? '#F0FBE8' : '#F5F0FF', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon size={16} strokeWidth={2} color={idx === 0 ? '#006FDD' : idx === 1 ? '#73D04B' : '#8B5CF6'}/>
                    </div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:800, color:'#1A1A2E', marginBottom:3 }}>{title}</div>
                      <div style={{ fontSize:13, color:'#6B7280', lineHeight:1.45 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:10, flexWrap:'wrap' }}>
                <button onClick={closeCheckoutTutorial} style={{ border:'1px solid #D1D5DB', background:'#fff', color:'#374151', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:700, cursor:'pointer' }}>Skip for now</button>
                <button onClick={startCheckoutTutorial} style={{ border:'none', background:'#006FDD', color:'#fff', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:800, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}>
                  Start setup <ArrowRight size={13} strokeWidth={2.4}/>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div style={{ position:'relative', zIndex:1, padding:'14px clamp(16px, 1.6vw, 28px) 0', width:'100%', boxSizing:'border-box', display:'grid', gap:12 }}>
        {billingAlert && (
          <div style={{
            border: `1px solid ${billingAlert.tone === 'error' ? '#FECACA' : '#FDE68A'}`,
            background: billingAlert.tone === 'error' ? '#FEF2F2' : '#FFFBEB',
            color: billingAlert.tone === 'error' ? '#991B1B' : '#92400E',
            borderRadius: 8,
            padding: '8px 11px',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: 12.5,
            lineHeight: 1.35,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
              <AlertTriangle size={15} strokeWidth={2.3} style={{ flexShrink:0 }}/>
              <span><strong>{billingAlert.title}.</strong> {billingAlert.detail}</span>
            </div>
            <button onClick={() => navigateWithQuery('settings', 'tab=billing')} style={{ border:'none', background:'transparent', color:'inherit', fontSize:12, fontWeight:900, cursor:'pointer', whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4 }}>
              Settings <ArrowRight size={12} strokeWidth={2.4}/>
            </button>
          </div>
        )}

        <section style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 1px 2px rgba(15,23,42,0.03)', padding:'9px 11px', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap:10, alignItems:'center' }}>
          <div style={{ border:`1px solid ${sourceToneStyle.border}`, background:sourceToneStyle.bg, borderRadius:7, padding:'8px 10px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, minWidth:0 }}>
            <button onClick={() => navigateWithQuery('sources')} style={{ border:0, background:'transparent', padding:0, display:'flex', alignItems:'center', gap:10, minWidth:0, textAlign:'left', cursor:'pointer' }}>
              <Upload size={15} strokeWidth={2.2} color={sourceToneStyle.icon} style={{ flexShrink:0 }}/>
              <span style={{ minWidth:0 }}>
                <span style={{ display:'flex', alignItems:'center', gap:8, minWidth:0, flexWrap:'wrap' }}>
                  <span style={{ fontSize:12.5, fontWeight:900, color:'#0F172A', whiteSpace:'nowrap' }}>Sources health</span>
                  {visibleSources.length ? visibleSources.slice(0, 4).map(source => {
                    const cfg = PLATFORM_CFG[source.id] || { label: source.id };
                    const StatusIcon = source.health.key === 'error' ? XCircle : source.health.key === 'warning' ? AlertTriangle : CheckCircle2;
                    const toneColor = source.health.key === 'error' ? HOME_TONE_STYLES.error.icon : source.health.key === 'warning' ? HOME_TONE_STYLES.warning.icon : '#64748B';
                    return (
                      <span key={source.id} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:12, color:'#475569', fontWeight:750, minWidth:0 }}>
                        <span style={{ maxWidth:96, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{cfg.label}</span>
                        <StatusIcon size={12} strokeWidth={2.5} color={toneColor}/>
                      </span>
                    );
                  }) : (
                    <span style={{ fontSize:12, color:'#64748B', fontWeight:750 }}>No live sources</span>
                  )}
                </span>
                <span style={{ display:'block', marginTop:2, fontSize:11.5, color:sourceToneStyle.color, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{sourceStatusDetail}</span>
              </span>
            </button>
            {sourceIssue ? (
              <button onClick={() => handleSourceAction(sourceIssue)} disabled={(sourceActions[sourceIssue.id] || {}).status === 'syncing'} style={{ border:`1px solid ${sourceToneStyle.border}`, background:'#fff', color:sourceToneStyle.color, borderRadius:7, padding:'6px 8px', fontSize:11.5, fontWeight:850, cursor:(sourceActions[sourceIssue.id] || {}).status === 'syncing' ? 'wait' : 'pointer', display:'inline-flex', alignItems:'center', gap:5, whiteSpace:'nowrap', flexShrink:0 }}>
                <RefreshCw size={12} strokeWidth={2.3} style={{ animation: (sourceActions[sourceIssue.id] || {}).status === 'syncing' ? 'spin 0.8s linear infinite' : 'none' }}/>
                {(sourceActions[sourceIssue.id] || {}).status === 'syncing' ? 'Syncing' : sourceIssue.connected && sourceIssue.syncPath ? 'Retry' : 'Reconnect'}
              </button>
            ) : !sourceCounts.total ? (
              <button onClick={() => navigateWithQuery('sources')} style={{ border:`1px solid ${sourceToneStyle.border}`, background:'#fff', color:sourceToneStyle.color, borderRadius:7, padding:'6px 8px', fontSize:11.5, fontWeight:850, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>Add source</button>
            ) : null}
          </div>

          <div style={{ border:`1px solid ${attentionToneStyle.border}`, background:attentionToneStyle.bg, borderRadius:7, padding:'8px 10px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, minWidth:0 }}>
            <button onClick={primaryNeedsRow ? () => navigateWithQuery(primaryNeedsRow.screen, primaryNeedsRow.query) : undefined} style={{ border:0, background:'transparent', padding:0, display:'flex', alignItems:'center', gap:10, minWidth:0, textAlign:'left', cursor:primaryNeedsRow ? 'pointer' : 'default' }}>
              {attentionTone === 'error'
                ? <XCircle size={15} strokeWidth={2.3} color={attentionToneStyle.icon} style={{ flexShrink:0 }}/>
                : attentionTone === 'warning'
                  ? <AlertTriangle size={15} strokeWidth={2.3} color={attentionToneStyle.icon} style={{ flexShrink:0 }}/>
                  : <CheckCircle2 size={15} strokeWidth={2.3} color={attentionToneStyle.icon} style={{ flexShrink:0 }}/>
              }
              <span style={{ minWidth:0 }}>
                <span style={{ display:'flex', alignItems:'baseline', gap:8, minWidth:0, flexWrap:'wrap' }}>
                  <span style={{ fontSize:12.5, fontWeight:900, color:'#0F172A', whiteSpace:'nowrap' }}>Needs attention</span>
                  <span style={{ fontSize:12, color:attentionToneStyle.color, fontWeight:850, whiteSpace:'nowrap' }}>{attentionSummary}</span>
                </span>
                <span style={{ display:'block', marginTop:2, fontSize:11.5, color:'#64748B', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{attentionDetail}</span>
              </span>
            </button>
            {primaryNeedsRow && (
              <button onClick={() => navigateWithQuery(primaryNeedsRow.screen, primaryNeedsRow.query)} style={{ border:`1px solid ${attentionToneStyle.border}`, background:'#fff', color:attentionToneStyle.color, borderRadius:7, padding:'6px 8px', fontSize:11.5, fontWeight:850, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5, whiteSpace:'nowrap', flexShrink:0 }}>
                Review <ArrowRight size={12} strokeWidth={2.4}/>
              </button>
            )}
          </div>
        </section>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:10 }}>
          {metricCards.map(card => <HomeKpiCard key={card.label} card={card}/>)}
        </div>

        <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'stretch' }}>
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden', boxShadow:'0 1px 2px rgba(15,23,42,0.03)', flex:'3 1 560px', minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, padding:'10px 13px', borderBottom:'1px solid #F3F4F6', flexWrap:'wrap' }}>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, fontWeight:850, color:'#0F172A' }}>
                  <Clock3 size={13} strokeWidth={2.2} color="#64748B"/>
                  Live order feed
                </div>
                <div style={{ fontSize:12, color:'#94A3B8', marginTop:2 }}>Latest normalized orders</div>
              </div>
              <button onClick={() => navigateWithQuery('orders')} style={{ border:'1px solid #E2E8F0', background:'#F8FAFC', color:'#334155', borderRadius:8, padding:'6px 8px', fontSize:12, fontWeight:800, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}>
                View all <ArrowRight size={13} strokeWidth={2.3}/>
              </button>
            </div>
            <div>
              {ordersLoading ? (
                <div style={{ display:'grid' }} aria-label="Loading recent orders" role="status">
                  {Array.from({ length: 10 }).map((_, idx) => (
                    <div key={idx} style={{ padding:'7px 12px', borderTop:idx ? '1px solid #F3F4F6' : 'none', display:'grid', gridTemplateColumns:'22px minmax(52px, 0.65fr) minmax(86px, 1fr) minmax(72px, 0.5fr) 76px 58px', gap:7, alignItems:'center', minHeight:38 }}>
                      <SkeletonPill width={22} height={22} style={{ borderRadius:6 }}/>
                      <SkeletonPill width="80%" height={12}/>
                      <SkeletonPill width="72%" height={12}/>
                      <SkeletonPill width={52} height={16}/>
                      <SkeletonPill width={62} height={13}/>
                      <SkeletonPill width={46} height={12}/>
                    </div>
                  ))}
                </div>
              ) : ordersError ? (
                <div style={{ padding:'22px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, color:'#94A3B8', flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:'#B91C1C', marginBottom:3 }}>Orders could not load</div>
                    <div style={{ fontSize:13 }}>Refresh the feed to try again.</div>
                  </div>
                  <button onClick={onRefresh} style={{ border:'1px solid #D1D5DB', background:'#fff', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:800, color:'#374151', cursor:'pointer', whiteSpace:'nowrap' }}>Retry</button>
                </div>
              ) : lastOrders.length > 0 ? lastOrders.map((order, idx) => {
                const d = parseOrderDate(order);
                const isNewOrder = (parseOrderSeenDate(order)?.getTime() || 0) > lastHomeSeenAt.getTime();
                const productLabel = order.product_name || order.product || order.email || order.id;
                const sourceLabel = PLATFORM_CFG[order.source]?.label || order.source || 'Source';
                const countryLabel = order.country ? countryDisplayName(order.country) : '-';
                const countryFlag = countryFlagSrc(order.country);
                return (
                  <button
                    key={order.id || order.source_id || idx}
                    onClick={() => navigateWithQuery('orders', order.id ? `order=${encodeURIComponent(order.id)}` : '')}
                    style={{
                      width:'100%',
                      border:0,
                      borderTop:idx ? '1px solid #F3F4F6' : 'none',
                      background:'#fff',
                      padding:'6px 12px',
                      minHeight:38,
                      textAlign:'left',
                      cursor:'pointer',
                      display:'grid',
                      gridTemplateColumns:'22px minmax(52px, 0.65fr) minmax(86px, 1fr) minmax(72px, 0.5fr) 76px 58px',
                      gap:7,
                      alignItems:'center',
                      animation: isNewOrder ? 'orderGlow 1.1s ease-out 0s 2' : 'none',
                    }}
                  >
                    <HomeSourceIcon source={order.source} size={22}/>
                    <span style={{ fontSize:12, fontWeight:800, color:'#475569', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0 }}>{sourceLabel}</span>
                    <span style={{ fontSize:12.5, fontWeight:800, color:'#1E293B', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0 }}>{productLabel}</span>
                    <span title={countryLabel} style={{ display:'flex', alignItems:'center', gap:6, minWidth:0 }}>
                      {countryFlag && (
                        <img src={countryFlag} alt="" aria-hidden="true" style={{ width:16, height:16, flexShrink:0 }}/>
                      )}
                      <span style={{ fontSize:12, fontWeight:750, color:'#475569', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0 }}>{countryLabel}</span>
                    </span>
                    <span style={{ fontSize:12.5, fontWeight:850, color:'#0F172A', whiteSpace:'nowrap', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>
                      {order.amount ? formatMoneyCompact(orderAmountNumber(order), order.currency) : '-'}
                    </span>
                    <span style={{ fontSize:11, color:'#94A3B8', whiteSpace:'nowrap', textAlign:'right' }}>{timeAgo(d, now)}</span>
                  </button>
                );
              }) : (
                <div style={{ padding:'22px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, color:'#94A3B8', flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:'#475569', marginBottom:3 }}>No active orders</div>
                    <div style={{ fontSize:13 }}>New paid or routed orders will appear here.</div>
                  </div>
                  <button onClick={() => navigateWithQuery('sources')} style={{ border:'1px solid #D1D5DB', background:'#fff', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:800, color:'#374151', cursor:'pointer', whiteSpace:'nowrap' }}>Add source</button>
                </div>
              )}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateRows:'minmax(142px, auto) minmax(164px, auto) minmax(0, 1fr)', gap:12, flex:'2 1 360px', minWidth:0, alignSelf:'stretch' }}>
            <section style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden', boxShadow:'0 1px 2px rgba(15,23,42,0.03)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, padding:'10px 13px', borderBottom:'1px solid #F3F4F6' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:850, color:'#0F172A' }}>Revenue trend</div>
                  <div style={{ fontSize:12, color:'#94A3B8', marginTop:2 }}>Daily revenue, last 30 days</div>
                </div>
                <div style={{ fontSize:12, fontWeight:850, color:'#475569', whiteSpace:'nowrap' }}>{revenueLoading ? <SkeletonPill width={68} height={14}/> : formatMoneyCompact(currentRevenue, revenueCurrency)}</div>
              </div>
              <div style={{ padding:'14px 13px 12px' }}>
                {revenueLoading ? (
                  <SkeletonPill width="100%" height={74} style={{ borderRadius:6 }}/>
                ) : (
                  <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:74 }}>
                    {dailyRevenue.map(point => (
                      <span key={point.key} title={`${point.date.toLocaleDateString()} ${formatMoneyCompact(point.revenue, revenueCurrency)}`} style={{ flex:'1 1 0', minWidth:2, height:`${Math.max(3, (point.revenue / maxDailyRevenue) * 74)}px`, background:point.revenue ? '#CBD5E1' : '#EEF2F7', borderRadius:'3px 3px 0 0' }}/>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden', boxShadow:'0 1px 2px rgba(15,23,42,0.03)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, padding:'10px 13px', borderBottom:'1px solid #F3F4F6' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:850, color:'#0F172A' }}>Top products</div>
                  <div style={{ fontSize:12, color:'#94A3B8', marginTop:2 }}>Count and revenue, last 30 days</div>
                </div>
              </div>
              <div style={{ display:'grid' }}>
                {revenueLoading ? Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} style={{ padding:'9px 13px', borderTop:idx ? '1px solid #F3F4F6' : 'none', display:'grid', gridTemplateColumns:'minmax(0, 1fr) 82px', gap:10, alignItems:'center' }}>
                    <SkeletonPill width="74%" height={12}/>
                    <SkeletonPill width={74} height={12}/>
                  </div>
                )) : topProducts.length ? topProducts.map((product, idx) => (
                  <div key={product.name} style={{ padding:'8px 13px', borderTop:idx ? '1px solid #F3F4F6' : 'none', display:'grid', gridTemplateColumns:'minmax(0, 1fr) 86px 92px', gap:9, alignItems:'baseline' }}>
                    <span style={{ fontSize:12.5, fontWeight:800, color:'#1E293B', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{product.name}</span>
                    <span style={{ fontSize:12, color:'#64748B', textAlign:'right', whiteSpace:'nowrap', fontVariantNumeric:'tabular-nums' }}>{formatHomeCount(product.count)} sold</span>
                    <span style={{ fontSize:12, color:'#0F172A', textAlign:'right', whiteSpace:'nowrap', fontWeight:850, fontVariantNumeric:'tabular-nums' }}>{formatMoneyCompact(product.revenue, revenueCurrency)}</span>
                  </div>
                )) : (
                  <div style={{ padding:'18px 13px', fontSize:12.5, color:'#64748B' }}>No product revenue in this period.</div>
                )}
              </div>
            </section>

            <section style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden', boxShadow:'0 1px 2px rgba(15,23,42,0.03)', minHeight:0, display:'grid', gridTemplateRows:'auto 1fr' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, padding:'10px 13px', borderBottom:'1px solid #F3F4F6' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:850, color:'#0F172A' }}>Recent exports</div>
                  <div style={{ fontSize:12, color:'#94A3B8', marginTop:2 }}>Today {formatHomeCount(exportedTodayCount)} · this week {formatHomeCount(exportedWeekCount)}</div>
                </div>
                <button onClick={() => navigateWithQuery('export')} style={{ border:'1px solid #E2E8F0', background:'#F8FAFC', color:'#334155', borderRadius:8, padding:'6px 8px', fontSize:12, fontWeight:800, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
                  Export <ArrowRight size={12} strokeWidth={2.3}/>
                </button>
              </div>
              <div style={{ display:'grid', alignContent:recentExports.length ? 'start' : 'center' }}>
                {ordersLoading ? Array.from({ length: 3 }).map((_, idx) => (
                  <div key={idx} style={{ padding:'9px 13px', borderTop:idx ? '1px solid #F3F4F6' : 'none', display:'grid', gridTemplateColumns:'22px minmax(0, 1fr) 62px', gap:8, alignItems:'center' }}>
                    <SkeletonPill width={22} height={22} style={{ borderRadius:6 }}/>
                    <SkeletonPill width="78%" height={12}/>
                    <SkeletonPill width={50} height={12}/>
                  </div>
                )) : recentExports.length ? recentExports.map(({ order, exportedAt }, idx) => {
                  const productLabel = order.product_name || order.product || order.email || order.id;
                  const sourceLabel = PLATFORM_CFG[order.source]?.label || order.source || 'Source';
                  return (
                    <button key={order.id || order.source_id || idx} onClick={() => navigateWithQuery('orders', order.id ? `order=${encodeURIComponent(order.id)}` : '')} style={{ border:0, borderTop:idx ? '1px solid #F3F4F6' : 'none', background:'#fff', padding:'8px 13px', display:'grid', gridTemplateColumns:'22px minmax(0, 1fr) 64px', gap:8, alignItems:'center', cursor:'pointer', textAlign:'left' }}>
                      <HomeSourceIcon source={order.source} size={22}/>
                      <span style={{ minWidth:0 }}>
                        <span style={{ display:'block', fontSize:12.5, fontWeight:800, color:'#1E293B', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{productLabel}</span>
                        <span style={{ display:'block', marginTop:2, fontSize:11.5, color:'#64748B', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{sourceLabel}</span>
                      </span>
                      <span style={{ fontSize:11, color:'#94A3B8', textAlign:'right', whiteSpace:'nowrap' }}>{timeAgo(exportedAt, now)}</span>
                    </button>
                  );
                }) : (
                  <div style={{ padding:'18px 13px', display:'flex', alignItems:'center', gap:9, color:'#64748B' }}>
                    <Download size={15} strokeWidth={2.2}/>
                    <div>
                      <div style={{ fontSize:13, fontWeight:850, color:'#475569' }}>No exports yet today</div>
                      <div style={{ fontSize:12, color:'#94A3B8', marginTop:2 }}>Exported orders will appear here.</div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
